import { EditOutlined, InfoCircleOutlined } from '@ant-design/icons';
import {
  OrderDoc,
  OrderProduct,
  OrderStatusCode,
  ProductDoc,
  ProductSnapshotDoc,
  StoreDoc,
} from '@gooduncles/gu-app-schema';
import { Alert, Button, Modal, Tooltip, message, notification } from 'antd';
import { FC, useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import {
  addProduct,
  removeProduct,
  resetProducts,
  setOrderProducts,
  setProductVolume,
} from 'src/reducers/actions/order-products';
import { initialState, orderProductsReducer } from 'src/reducers/order-products';

import { formatNumber } from 'src/utils/common';
import { getProduct } from 'src/utils/firebase/firebase-short-cut';
import {
  OrderWithStore,
  getCurrentOrderStatus,
  getDateForAcceptedOrder,
  isOrderCreationBlockedTime,
} from 'src/utils/order-util';
import { getProductBaseFromProduct } from 'src/utils/product-util';

import useOrderProductsForStore from 'src/hooks/swr/useOrderProductsForStore';
import useStoresForSearch from 'src/hooks/swr/useStoresForSearch';
import useOrder from 'src/hooks/useOrder';

import { useCommerceConf } from 'src/redux/hooks/useCommerceConf';

import AlgoliaSearch from 'src/atomic-components/molecules/AlgoriaSearch/AlgoriaSearch';
import FlexRowItem from 'src/components/Common/FlexRowItem/FlexRowItem';
import OrderProductItem from 'src/components/Common/OrderProductItem/OrderProductItem';
import SearchWithPreview from 'src/components/Common/SearchWithPreview/SearchWithPreview';

import classes from './EditOrderModal.module.scss';

const getOrderProduct = (product: ProductDoc | ProductSnapshotDoc): OrderProduct => {
  const productBase = getProductBaseFromProduct(product);
  return {
    ...productBase,
    volume: 1,
    snapshotId: null,
    snapshotPrice: null,
    priceDiff: null,
  };
};

interface EditOrderModalProps {
  ordersWithStore: OrderWithStore[];
  disabled: boolean;
}

const EditOrderModal: FC<EditOrderModalProps> = ({ ordersWithStore, disabled }) => {
  const cartRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [state, dispatch] = useReducer(orderProductsReducer, initialState);
  const [matchedOrder, setMatchedOrder] = useState<OrderDoc | null>(null);
  const { isLoading, onCreateOrder, onUpdateOrderDoc } = useOrder();

  const [selectedStore, setSelectedStore] = useState<StoreDoc | null>(null);
  const { orderProducts, orderProductsLoading } = useOrderProductsForStore(selectedStore?._id ?? null);

  const { stores, storeSearchValues } = useStoresForSearch([['state', '!=', 'closed']]);

  const { commerceConf } = useCommerceConf();
  const [now, setNow] = useState(() => new Date());

  // 모달이 열려있는 동안 1분마다 현재 시각을 갱신해 예상 상태/배송일을 최신화한다.
  useEffect(() => {
    if (!open) return;
    setNow(new Date());
    const intervalId = setInterval(() => setNow(new Date()), 60 * 1000);
    return () => clearInterval(intervalId);
  }, [open]);

  // 신규 주문 생성 시 현재 시각 기준 예상 주문 상태와 배송일을 계산한다.
  const orderPreview = useMemo(() => {
    if (!commerceConf?.deliveryDateRules) return null;
    if (isOrderCreationBlockedTime(now)) {
      return { blocked: true as const };
    }
    const [predictedStatus] = getCurrentOrderStatus(commerceConf.deliveryDateRules);
    const predictedDeliveryDate =
      predictedStatus === OrderStatusCode.ACCEPTED ? getDateForAcceptedOrder(commerceConf) : null;
    return { blocked: false as const, status: predictedStatus, deliveryDate: predictedDeliveryDate };
  }, [commerceConf, now]);

  const onStoreSearch = useCallback(
    (value: string) => {
      const selectedStoreCode = value.split('-')?.[0] ?? null;
      if (selectedStoreCode) {
        const store = stores?.find((store) => selectedStoreCode === store.storeCode);
        if (store) {
          message.success(store.storeNickname);
          dispatch(resetProducts());
          setSelectedStore(store);
        } else {
          notification.error({ message: '매장을 찾을 수 없습니다.' });
          setSelectedStore(null);
        }
      }
    },
    [stores],
  );

  // 추후 다른 주문을 생성하기 위해서는 snapshot이 필요하기 때문.
  const onProductSearch = useCallback(
    async (value: string) => {
      const selectedProductId = value.split('_')?.[0] ?? null;
      if (selectedProductId) {
        if (state.orderProducts[selectedProductId]) {
          notification.info({ message: '이미 추가된 상품입니다.' });
          return;
        }

        try {
          const product = await getProduct(selectedProductId);
          if (product) {
            const orderProduct = getOrderProduct(product);
            dispatch(addProduct(orderProduct));
            message.success(orderProduct.fullName);
          } else {
            notification.error({ message: '상품을 찾을 수 없습니다.' });
          }
        } catch (error) {
          notification.error({ message: '에러 발생' });
        }
      }
    },
    [state.orderProducts],
  );

  const onVolumeChange = (productId: string, volume: number) => {
    dispatch(setProductVolume({ productId, volume }));
  };

  const onRemove = (productId: ProductDoc['_id']) => {
    dispatch(removeProduct(productId));
  };

  const reset = useCallback((showMsg = true) => {
    setSelectedStore(null);
    setMatchedOrder(null);
    dispatch(resetProducts());
    if (showMsg) {
      message.success('초기화 완료');
    }
  }, []);

  const onOpen = useCallback(() => {
    setOpen(true);
  }, []);

  const onCancel = useCallback(() => {
    reset(false);
    setOpen(false);
  }, [reset]);

  /**
   * 최종 주문 액션을 처리한다.
   */
  const onEditOrder = useCallback(async () => {
    if (!selectedStore || !state.orderProducts) {
      notification.error({ message: '매장 또는 장바구니 정보가 없습니다.' });
      return;
    }

    if (!selectedStore.storeManagerId) {
      notification.error({ message: '매장 관리자 정보가 없습니다.' });
      return;
    }

    if (matchedOrder) {
      await onUpdateOrderDoc(selectedStore, matchedOrder, Object.values(state.orderProducts));
      reset(false);
      setOpen(false);
      return;
    }

    // 자동 수락 시간대(23:05 ~ 09:00)에 신규 주문을 생성하면 즉시 '수락완료'로 등록되므로
    // 운영자가 의도치 않게 9시 자동화에 포함시키는 사고를 막기 위해 한번 더 확인한다.
    if (orderPreview && !orderPreview.blocked && orderPreview.status === OrderStatusCode.ACCEPTED) {
      const confirmed = await new Promise<boolean>((resolve) => {
        Modal.confirm({
          title: '"수락완료" 상태로 즉시 등록됩니다.',
          content: `현재 시각이 자동 수락 시간대(23:05 ~ 09:00)라 이 주문은 9시 자동화에 포함돼요.${
            orderPreview.deliveryDate ? `\n배송 예정일: ${orderPreview.deliveryDate}` : ''
          }\n그래도 주문을 생성할까요?`,
          okText: '생성',
          cancelText: '취소',
          okButtonProps: { danger: true },
          onOk: () => resolve(true),
          onCancel: () => resolve(false),
        });
      });
      if (!confirmed) return;
    }

    const result = await onCreateOrder(selectedStore.storeManagerId, selectedStore, Object.values(state.orderProducts));
    if (result) {
      reset(false);
      setOpen(false);
    }
  }, [selectedStore, state.orderProducts, matchedOrder, orderPreview, reset, onUpdateOrderDoc, onCreateOrder]);

  useEffect(() => {
    if (cartRef.current) {
      cartRef.current.scrollTop = cartRef.current.scrollHeight;
    }
  }, [state.length]);

  // 입력하려는 매장의 주문이 이미 존재하는지 확인하고, 존재한다면 해당 주문을 불러온다.
  useEffect(() => {
    if (open) {
      const order = ordersWithStore
        .filter((o) => [OrderStatusCode.SUBMITTED, OrderStatusCode.ACCEPTED].includes(o.orderStatus))
        .find((s) => s.store?.storeCode === selectedStore?.storeCode);
      if (order) {
        setMatchedOrder((prev) => {
          if (prev?._id === order._id && prev?._timeUpdate?.seconds === order._timeUpdate?.seconds) {
            return prev;
          }
          return order;
        });
      } else {
        setMatchedOrder(null);
      }
    }
  }, [open, ordersWithStore, selectedStore?.storeCode]);

  useEffect(() => {
    if (open) {
      if (matchedOrder) {
        message.info('주문을 동기화합니다.');
        dispatch(setOrderProducts(matchedOrder.products));
      } else {
        dispatch(resetProducts());
      }
    }
  }, [open, matchedOrder]);

  return (
    <>
      <div className={classes.tableButtons}>
        <Tooltip title="'수락대기', '주문수락' 체크박스를 모두 선택한 후 사용할 수 있습니다.">
          <Button onClick={onOpen} type='primary' disabled={disabled} icon={<EditOutlined />}>
            주문 편집기
          </Button>
        </Tooltip>
      </div>
      <Modal
        open={open}
        title={
          <Tooltip title="사용법: 매장 검색 → 선택 → 상품 검색 → 선택 → 추가된 상품의 수량조절 → '주문 생성'">
            주문 편집기 <InfoCircleOutlined />
          </Tooltip>
        }
        onCancel={onCancel}
        width='900px'
        centered
        keyboard={false}
        maskClosable={false}
        okText={matchedOrder ? '주문 수정' : '주문 생성'}
        cancelText='닫기'
        confirmLoading={isLoading}
        onOk={onEditOrder}
        okButtonProps={{ disabled: state.length < 1 || (orderPreview?.blocked && !matchedOrder) }}
      >
        {!matchedOrder && orderPreview?.blocked && (
          <Alert
            className={classes.orderPreviewBanner}
            type='error'
            showIcon
            message='지금은 주문을 생성할 수 없습니다.'
            description='23:00 ~ 23:05는 자동 주문 수락 처리 시간이에요. 23:05 이후 다시 시도해주세요.'
          />
        )}
        {!matchedOrder && orderPreview && !orderPreview.blocked && orderPreview.status === OrderStatusCode.ACCEPTED && (
          <Alert
            className={classes.orderPreviewBanner}
            type='warning'
            showIcon
            message='지금 생성하면 "수락완료" 상태로 즉시 등록됩니다.'
            description={`현재 시각이 자동 수락 시간대(23:05 ~ 09:00)라 9시 자동화에 포함돼요.${
              orderPreview.deliveryDate ? ` 배송 예정일: ${orderPreview.deliveryDate}` : ''
            }`}
          />
        )}
        {!matchedOrder &&
          orderPreview &&
          !orderPreview.blocked &&
          orderPreview.status === OrderStatusCode.SUBMITTED && (
            <Alert
              className={classes.orderPreviewBanner}
              type='info'
              showIcon
              message='"주문완료" 상태로 생성됩니다. (이후 23:05에 자동 수락 처리)'
            />
          )}
        <div className={classes.editOrderContainer}>
          <div className={classes.searchPanel}>
            <FlexRowItem label='매장' labelStyle={{ color: 'var(--gray800)', fontWeight: 700 }} />
            <SearchWithPreview
              rowData={storeSearchValues}
              onSearch={onStoreSearch}
              selectOnly={true}
              closeOnClick={true}
              placeholder='매장코드 또는 매장명(관리)으로 검색'
            />
            <div className={classes.storeInfo}>
              <FlexRowItem
                label='매장명(관리)'
                value={selectedStore?.storeNickname ?? '미입력'}
                valueStyle={!selectedStore?.storeNickname ? { color: 'red' } : undefined}
              />
              <FlexRowItem
                label='매장코드'
                value={selectedStore?.storeCode ?? '미입력'}
                valueStyle={!selectedStore?.storeCode ? { color: 'red' } : undefined}
              />
            </div>
            <FlexRowItem
              label={selectedStore ? '상품' : '매장을 먼저 선택해주세요.'}
              labelStyle={{ color: 'var(--gray800)', fontWeight: 700 }}
            />
            <AlgoliaSearch placeholder='상품코드 또는 상품명으로 검색' onSearch={onProductSearch} />
            <p className={classes.tip}>※ 상품 선택시 오른쪽 장바구니에 추가됩니다.</p>

            <FlexRowItem
              label={
                selectedStore
                  ? `이전 주문 상품: ${
                      orderProductsLoading ? '읽는중...' : orderProducts?.length ? `${orderProducts?.length}개` : '없음'
                    }`
                  : '매장을 먼저 선택해주세요.'
              }
              labelStyle={{ color: 'var(--gray800)', fontWeight: 700 }}
            />
            <SearchWithPreview
              rowData={orderProducts?.map((p) => `${p.productId}_${p.fullName}`) ?? null}
              onSearch={onProductSearch}
              selectOnly={true}
              placeholder={!orderProducts?.length ? '3개월간 주문한 상품이 없습니다.' : '상품코드 또는 상품명으로 검색'}
              disabled={!orderProducts?.length}
            />
            {/* 즐겨찾기 상품 추가 */}
            <div className={classes.actions}>
              <Button onClick={() => reset()}>입력정보 초기화</Button>
            </div>
          </div>

          <div className={classes.cartPanel}>
            <FlexRowItem label='장바구니' labelStyle={{ color: 'var(--gray800)', fontWeight: 700 }} />
            <div className={classes.cartContainer} ref={cartRef}>
              <div className={classes.marginBox} />
              {state.orderProducts &&
                Object.entries(state.orderProducts).map(([key, product]) => (
                  <OrderProductItem
                    key={key}
                    orderProduct={product}
                    onVolumeChange={onVolumeChange}
                    onRemove={onRemove}
                    style={{ borderBottom: '1px dashed var(--gray200)' }}
                  />
                ))}
            </div>
            <div className={classes.summary}>
              <FlexRowItem
                label='총 주문금액'
                value={`${formatNumber(state.totalPrice)} 원`}
                valueStyle={{
                  fontSize: '16px',
                  fontWeight: '600',
                  textAlign: 'right',
                }}
              />
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default EditOrderModal;
